<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import dateFormatUtil from "@/shared/lib/date-format";
import { getLogList } from "@/entities/logging";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import Button from "../button/Button.vue";

const props = defineProps({
  entity: {
    type: Object,
    default: () => null,
  },
  idField: {
    type: String,
    default: "id",
  },
  service: {
    type: String,
    required: true,
  },
  apiPath: {
    type: String,
    required: true,
  },
});

const { locale } = useI18n();

const loading = ref(false);
const logs = ref([]);
const total = ref(0);
const pageSize = ref(10);
const currentPage = ref(1);

const currentLang = computed(() => locale.value);

const actionKey = computed(() => {
  return {
    kk: "action_description_kz",
    ru: "action_description",
    en: "action_description_en",
  }[currentLang.value];
});

watch(
  () => props.entity?.[props.idField],
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      currentPage.value = 1;
      getLogs();
    }
  },
  { immediate: true }
);

async function getLogs() {
  const params = {
    limit: pageSize.value,
    offset: (currentPage.value - 1) * pageSize.value,
    service: props.service,
    path: props.apiPath,
    service_record_id: props.entity?.[props.idField],
    service_record_only: true,
  };
  loading.value = true;
  try {
    const data = await getLogList(params);
    if (data) {
      logs.value = data.items || [];
      total.value = data.total || logs.value.length;
    }
  } finally {
    loading.value = false;
  }
}

function handlePageChange(page) {
  currentPage.value = page;
  getLogs();
}

function handlePageSizeChange(size) {
  pageSize.value = size;
  currentPage.value = 1;
  getLogs();
}

// --- Mobile "load more" ---
const hasMore = computed(() => logs.value.length < total.value);
const loadingMore = ref(false);

async function loadMore() {
  const offset = logs.value.length;
  const params = {
    limit: pageSize.value,
    offset,
    service: props.service,
    path: props.apiPath,
    service_record_id: props.entity?.[props.idField],
    service_record_only: true,
  };
  loadingMore.value = true;
  try {
    const data = await getLogList(params);
    if (data?.items?.length) {
      logs.value = [...logs.value, ...data.items];
    }
  } finally {
    loadingMore.value = false;
  }
}

function dateFormat(date) {
  if (!date) return null;
  return dateFormatUtil.timestamp_to_local_tz(date, "DD.MM.YYYY HH:mm");
}
</script>

<template>
  <div class="h-full flex flex-col gap-4">
    <!-- Desktop: table with scroll -->
    <div class="table-area">
      <div class="table-scroll">
        <Table :loading="loading" :scroll-reset-key="currentPage">
          <TableHeader>
            <TableRow>
              <TableHead class="w-[204px]">{{ $t("Дата") }}</TableHead>
              <TableHead class="min-w-[319px]">{{ $t("Событие") }}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableEmpty v-if="!loading && !logs.length" :colspan="2">
              {{ $t("Нет данных") }}
            </TableEmpty>
            <TableRow v-for="row in logs" :key="row.id || row.timestamp">
              <TableCell>
                <div v-if="row.timestamp" class="text-sm text-foreground">
                  {{ dateFormat(row.timestamp) }}
                </div>
                <div v-else>{{ $t("Не указано") }}</div>
              </TableCell>
              <TableCell>
                <div v-if="row[actionKey]" class="text-sm text-foreground">
                  {{ row[actionKey] }}
                </div>
                <div v-else>{{ $t("Не указано") }}</div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <TableFooterWrapper
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50]"
        @update:page-size="handlePageSizeChange"
        @update:current-page="handlePageChange"
      >
        <template #pagination>
          <Pagination
            v-show="total > pageSize"
            class="w-auto mx-0 justify-start"
            :total="total"
            :items-per-page="pageSize"
            :page="currentPage"
            :sibling-count="1"
            :disabled="loading"
            show-edges
            @update:page="handlePageChange"
          >
            <PaginationContent v-slot="{ items }">
              <PaginationPrevious />
              <template v-for="(item, index) in items" :key="index">
                <PaginationItem
                  v-if="item.type === 'page'"
                  :value="item.value"
                  :is-active="item.value === currentPage"
                />
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </Pagination>
        </template>

        <template #extra>
          <div class="flex items-center gap-3">
            <span class="text-sm leading-5 text-gray-600">{{
              $t("Изменения")
            }}</span>
            <span v-if="logs.length" class="text-sm leading-5 text-gray-600">
              {{ dateFormat(logs[0].timestamp) }}
            </span>
          </div>
        </template>
      </TableFooterWrapper>
    </div>

    <!-- Mobile: cards with scroll -->
    <div class="history-list">
      <div v-if="loading" class="flex items-center justify-center py-6">
        <Spinner size="sm" />
      </div>
      <Empty v-else-if="!logs.length" :description="$t('Нет истории')" />
      <div v-else class="history-cards-scroll">
        <div v-for="log in logs" :key="log.id" class="history-card">
          <div class="history-date">
            {{ log.timestamp ? dateFormat(log.timestamp) : $t("Не указано") }}
          </div>
          <div class="history-text">
            {{ log[actionKey] || $t("Не указано") }}
          </div>
        </div>

        <Button
          v-if="hasMore"
          :disabled="loadingMore"
          @click="loadMore"
          variant="outline"
        >
          <Spinner v-if="loadingMore" size="sm" />
          <span v-else>{{ $t("Показать ещё") }}</span>
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-area {
  @apply w-full flex flex-col;
  min-height: 0;
  flex: 1;
}

.table-scroll {
  @apply flex-1 overflow-auto;
  min-height: 0;
}

.history-list {
  display: none;
  flex-direction: column;
  @apply w-full;
  min-height: 0;
  flex: 1;
}

.history-cards-scroll {
  @apply flex flex-col gap-4 flex-1 overflow-auto;
  min-height: 0;
}

.history-card {
  @apply w-full bg-card rounded-2xl border border-border p-4;
  @apply flex flex-col gap-2 flex-shrink-0;
}

.history-date {
  @apply text-sm font-semibold text-foreground;
}

.history-text {
  @apply text-sm text-foreground;
}

@media (max-width: 900px) {
  .table-area {
    display: none;
  }

  .history-list {
    display: flex;
  }
}
</style>
